import { MacWindow } from './MacWindow';
import React, { useState } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { Settings, AppWindow, ExternalLink, ShieldCheck, Sun, Moon, Palette, Sparkles } from 'lucide-react';
import { playfulAudio } from '../../utils/playfulAudio';
import { AppLogo } from './AppLogo';
import { LogoShowcaseModal } from './LogoShowcaseModal';
import { GlassmorphismShowcase } from '../common/GlassmorphismCard';

export const UserProfileSettings = () => {
  const { showSettings, setShowSettings, appUnit, setAppUnit, theme, setTheme } = useSaaSContext();
  const [showLogoModal, setShowLogoModal] = useState(false);

  const handleSelectTheme = (newTheme: 'dark' | 'light' | 'glassmorphism') => {
    playfulAudio.playCutePop(newTheme === 'dark' ? 0.9 : newTheme === 'light' ? 1.2 : 1.4);
    setTheme(newTheme);
  };

  if (!showSettings) return null;

  return (
    <MacWindow isOpen={showSettings} onClose={() => setShowSettings(false)} title="App Settings & Preferences" maxWidth="max-w-2xl">
      <div className="flex flex-col w-full overflow-y-auto overflow-x-hidden max-h-[85vh]">
        
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 z-10 bg-zinc-950">
          <div className="flex items-center gap-2.5 text-zinc-100">
            <Settings className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-bold">Preferences & Configuration</h2>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-6">
          {/* Brand Identity & Logo Card */}
          <div className="bg-zinc-900 border border-blue-500/30 p-4 rounded-xl flex items-center justify-between gap-4 shadow-sm">
            <div className="flex items-center gap-3">
              <AppLogo size={40} animated={true} />
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-bold text-zinc-100">Variable Sticker Studio</h3>
                  <span className="text-[9px] px-1.5 py-0.2 rounded font-mono font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    Official Logo
                  </span>
                </div>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  High-resolution vector holographic sticker emblem with data matrix accents.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowLogoModal(true)}
              className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
            >
              <Sparkles className="w-3.5 h-3.5 text-blue-400" />
              <span>Export Logo</span>
            </button>
          </div>

          {/* Appearance / Theme Selector Section */}
          <div>
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Palette className="w-4 h-4 text-amber-400" /> Workspace Appearance & Theme
            </h3>
            <p className="text-xs text-zinc-400 mb-3">
              Choose your preferred visual theme. Both modes maintain strict contrast standards for flawless readability.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Dark Theme Option */}
              <button
                type="button"
                onClick={() => handleSelectTheme('dark')}
                className={`p-3 rounded-xl border flex items-center gap-2.5 transition-all cursor-pointer text-left ${
                  theme === 'dark'
                    ? 'bg-zinc-800 border-blue-500 ring-2 ring-blue-500/30 text-white shadow-md'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                }`}
              >
                <div className="w-7 h-7 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-center text-indigo-400 shrink-0">
                  <Moon className="w-3.5 h-3.5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-zinc-100 flex items-center gap-1.5">
                    <span>Dark Mode</span>
                    {theme === 'dark' && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
                  </div>
                  <div className="text-[10px] text-zinc-400">Pro Studio Dark</div>
                </div>
              </button>

              {/* Light Theme Option */}
              <button
                type="button"
                onClick={() => handleSelectTheme('light')}
                className={`p-3 rounded-xl border flex items-center gap-2.5 transition-all cursor-pointer text-left ${
                  theme === 'light'
                    ? 'bg-white border-amber-500 ring-2 ring-amber-500/30 text-slate-900 shadow-md'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                }`}
              >
                <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500 shrink-0">
                  <Sun className="w-3.5 h-3.5" />
                </div>
                <div>
                  <div className="text-xs font-bold text-zinc-100 flex items-center gap-1.5">
                    <span>Light Mode</span>
                    {theme === 'light' && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                  </div>
                  <div className="text-[10px] text-zinc-400">Studio Clean Light</div>
                </div>
              </button>

              {/* Aurora Glassmorphism Option */}
              <button
                type="button"
                onClick={() => handleSelectTheme('glassmorphism')}
                className={`p-3 rounded-xl border flex items-center gap-2.5 transition-all cursor-pointer text-left ${
                  theme === 'glassmorphism'
                    ? 'bg-purple-950/50 border-purple-400 ring-2 ring-purple-500/40 text-white shadow-lg backdrop-blur-md'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                }`}
              >
                <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-purple-600/30 to-pink-500/30 border border-purple-400/40 flex items-center justify-center text-pink-300 shrink-0 backdrop-blur-sm">
                  <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                </div>
                <div>
                  <div className="text-xs font-bold text-zinc-100 flex items-center gap-1.5">
                    <span>Glassmorphism</span>
                    {theme === 'glassmorphism' && <span className="w-1.5 h-1.5 rounded-full bg-pink-400" />}
                  </div>
                  <div className="text-[10px] text-zinc-400">Frosted Glass & Aurora</div>
                </div>
              </button>
            </div>
          </div>

          {/* Default Measurement Unit */}
          <div>
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <AppWindow className="w-4 h-4 text-blue-400" /> Default Measurement Unit
            </h3>
            <p className="text-xs text-zinc-400 mb-3">
              Select your preferred dimension unit for VDP design canvas calculations, die-lines, margins, and imposition layouts.
            </p>
            <div className="grid grid-cols-4 gap-2 bg-zinc-900 border border-zinc-800 p-2 rounded-lg">
              {(['pt', 'mm', 'cm', 'in'] as const).map((unit) => (
                <button
                  key={unit}
                  onClick={() => setAppUnit(unit)}
                  className={`py-2 px-3 text-xs font-bold rounded-md transition-all uppercase cursor-pointer ${
                    appUnit === unit
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  {unit}
                </button>
              ))}
            </div>
          </div>

          {/* Aurora Glassmorphism Design Showcase */}
          <div>
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" /> UI Design Showcase: Aurora Glassmorphism Card
            </h3>
            <p className="text-xs text-zinc-400 mb-3">
              High-end Glassmorphic translucent card with 60% blur, 20% opacity white fill, glowing white border, and dynamic Aurora gradient.
            </p>
            <GlassmorphismShowcase />
          </div>

          {/* Legal & Policy Pages Links */}
          <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-xl flex flex-col gap-3">
            <h3 className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Legal &amp; Compliance URLs
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <a
                href="#privacy"
                onClick={() => setShowSettings(false)}
                className="p-3 bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 rounded-lg flex items-center justify-between group transition-colors"
              >
                <div>
                  <div className="font-bold text-zinc-200 group-hover:text-blue-400 transition-colors">Privacy Policy</div>
                  <div className="text-[10px] text-zinc-500 font-mono">/#privacy or ?legal=privacy</div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500 group-hover:text-blue-400 transition-colors" />
              </a>

              <a
                href="#terms"
                onClick={() => setShowSettings(false)}
                className="p-3 bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 rounded-lg flex items-center justify-between group transition-colors"
              >
                <div>
                  <div className="font-bold text-zinc-200 group-hover:text-blue-400 transition-colors">Terms of Service</div>
                  <div className="text-[10px] text-zinc-500 font-mono">/#terms or ?legal=terms</div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-500 group-hover:text-blue-400 transition-colors" />
              </a>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex justify-end items-center">
          <button 
            onClick={() => setShowSettings(false)}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-md"
          >
            Save & Close
          </button>
        </div>
      </div>

      <LogoShowcaseModal 
        isOpen={showLogoModal} 
        onClose={() => setShowLogoModal(false)} 
      />
    </MacWindow>
  );
};
